import React, { useState, useEffect, useCallback } from 'react'
import styled from 'styled-components'
import type { FeaturedProduct } from '@src/types'
import { colors } from '@src/theme/sharedComponents'
import FeaturedProductCard from './FeaturedProductCard'

const AUTO_ADVANCE_MS = 4500

const Strip = styled.div`
  position: relative;
  width: 100%;
  height: 130px;
  overflow: hidden;
`

const SlideLayer = styled.div<{ $active: boolean }>`
  position: absolute;
  inset: 0;
  opacity: ${p => p.$active ? 1 : 0};
  transition: opacity 0.4s ease;
  pointer-events: ${p => p.$active ? 'all' : 'none'};
`

const NavColumn = styled.div`
  position: absolute;
  top: 0;
  right: 0;
  bottom: 0;
  width: 32px;
  z-index: 3;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 0.2rem;
  background: rgba(0,0,0,0.25);
  border-left: 1px solid rgba(255,255,255,0.1);
`

const NavArrow = styled.button`
  width: 26px;
  height: 26px;
  border: none;
  border-radius: 0;
  background: rgba(255,255,255,0.1);
  color: white;
  font-size: 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s;
  flex-shrink: 0;
  &:hover { background: ${colors.accent}; color: ${colors.primaryDark}; }
`

const DotsColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 3px;
`

const Dot = styled.button<{ $active: boolean }>`
  padding: 0;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${p => p.$active ? colors.accent : 'rgba(255,255,255,0.3)'};
  width: 3px;
  height: ${p => p.$active ? '14px' : '3px'};
  border-radius: 0;
  flex-shrink: 0;
  &:hover { background: ${p => p.$active ? colors.accent : 'rgba(255,255,255,0.6)'}; }
`

interface FeaturedProductStripProps {
  products: FeaturedProduct[]
  categoryImageMap: Record<string, string>
  onProductClick: (fp: FeaturedProduct) => void
}

const FeaturedProductStrip: React.FC<FeaturedProductStripProps> = ({ products, categoryImageMap, onProductClick }) => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [paused, setPaused] = useState(false)
  const slideCount = products.length

  const next = useCallback(() => setCurrentSlide(p => (p + 1) % slideCount), [slideCount])
  const prev = useCallback(() => setCurrentSlide(p => (p - 1 + slideCount) % slideCount), [slideCount])

  useEffect(() => {
    if (paused || slideCount <= 1) return
    const t = setInterval(next, AUTO_ADVANCE_MS)
    return () => clearInterval(t)
  }, [paused, slideCount, next])

  if (slideCount === 0) return null

  return (
    <Strip onMouseEnter={() => setPaused(true)} onMouseLeave={() => setPaused(false)}>
      {products.map((fp, si) => (
        <SlideLayer key={fp.id ?? si} $active={si === currentSlide}>
          <FeaturedProductCard
            fp={fp}
            onClick={() => onProductClick(fp)}
            categoryImageMap={categoryImageMap}
            style={{ height: '100%', borderRadius: 0 }}
          />
        </SlideLayer>
      ))}
      {slideCount > 1 && (
        <NavColumn>
          <NavArrow onClick={e => { e.stopPropagation(); prev() }} aria-label="Previous"><i className="bi bi-chevron-up" /></NavArrow>
          <DotsColumn>
            {products.map((_, di) => (
              <Dot key={di} $active={di === currentSlide} onClick={e => { e.stopPropagation(); setCurrentSlide(di) }} aria-label={`Go to slide ${di + 1}`} />
            ))}
          </DotsColumn>
          <NavArrow onClick={e => { e.stopPropagation(); next() }} aria-label="Next"><i className="bi bi-chevron-down" /></NavArrow>
        </NavColumn>
      )}
    </Strip>
  )
}

export default FeaturedProductStrip
